import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Avatar, IconButton, Collapse } from '@mui/material';
import { Copy, Check } from 'lucide-mui';
import { useTheme, styled, alpha } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { RootState } from '../../../../reducers';
import { onClickTimestamp } from '../../../../lib/commonUtils';
import Thinking from './Thinking';
import newLogo from '../../../../assets/icons/new_logo.svg';
import ChatHistoryEmpty from './ChatHistoryEmpty';

const TimestampBox = styled('span')(({ theme }) => ({
  position: 'relative',
  cursor: 'pointer',
  display: 'inline-block',
  color: theme.palette.getContrastText(theme.palette.secondary.light),
  backgroundColor: alpha(theme.palette.secondary.light, 0.9),
  fontSize: '0.8125rem',
  padding: '1px 6px',
  margin: '0 4px',
  borderRadius: '4px',
  transition: 'all 0.15s ease',
  letterSpacing: '0.01em',
  fontWeight: 500,
  userSelect: 'none',
  '&:hover': {
    backgroundColor: theme.palette.secondary.light,
    transform: 'translateY(-1px)',
  },
}));

const ChatHistory = () => {
  const theme = useTheme();
  const { selectedVideo, settings } = useSelector((state: RootState) => state);
  const { status, conversation } = selectedVideo.chat;
  const { user } = settings;
  const { picture } = user;

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [isThinking, setIsThinking] = useState(false);
  const [expandingResponse, setExpandingResponse] = useState<string | null>(
    null
  );
  const [showFullResponse, setShowFullResponse] = useState(false);

  useEffect(() => {
    console.log('Chat History Status:', status);
    console.log('Full Conversation History:', conversation);
    console.log('Latest Message:', conversation[conversation.length - 1]);
  }, [status, conversation]);

  useEffect(() => {
    if (status === 'pending') {
      console.log('Thinking state started');
      setIsThinking(true);
      setExpandingResponse(null);
    } else if (status === 'succeeded' && isThinking) {
      console.log('Thinking state ended');
      const lastMessage = conversation[conversation.length - 1];
      if (lastMessage && lastMessage.role === 'assistant') {
        console.log('New assistant response:', lastMessage.content);
        setExpandingResponse(lastMessage.content);
        setIsThinking(false);
      } else {
        setIsThinking(false);
      }
    }
  }, [status, conversation, isThinking]);

  const getAvatar = (role: string) => {
    if (role === 'user') {
      return <Avatar sx={{ marginRight: '1rem' }} src={picture} alt="You" />;
    }
    return (
      <Avatar
        sx={{
          marginRight: '1rem',
          bgcolor: 'transparent',
        }}
        src={newLogo}
        alt="ScreenApp"
      />
    );
  };

  const handleCopy = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleTimestampClick = (seconds: number) => {
    console.log('Timestamp clicked:', seconds);
    onClickTimestamp(seconds);
  };

  const formatTimeComponent = (parts: number[]) => {
    if (parts.length === 3) {
      const [h, m, s] = parts;
      return h > 0
        ? `${h}:${m}:${s.toString().padStart(2, '0')}`
        : `${m}:${s.toString().padStart(2, '0')}`;
    }
    const [m, s] = parts.slice(-2);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const convertTimestampsToLinks = (text: string) => {
    const timestampRegex = /[[(]?\s*(?:(?:\d+:)?\d{2}|\d):\d{2}\s*[)\]]?/g;
    const parts = text.split(timestampRegex);
    const matches = text.match(timestampRegex);

    if (!matches) return text;

    const getFullTimestamp = (timeParts: number[]) => {
      if (timeParts.length === 3) {
        return `${timeParts[0]}h ${timeParts[1]}m ${timeParts[2]}s`;
      }
      if (timeParts.length === 2) {
        return `${timeParts[0]}m ${timeParts[1]}s`;
      }
      return `${timeParts[0]}s`;
    };

    return parts.flatMap((part, index) => {
      if (index < matches.length) {
        const match = matches[index];
        const cleanMatch = match.replace(/[[\]()\s]/g, '');
        const timeParts = cleanMatch.split(':').map(Number);
        let seconds;

        if (timeParts.length === 3) {
          const [hours, minutes, secs] = timeParts;
          seconds = hours * 3600 + minutes * 60 + secs;
        } else if (timeParts.length === 2) {
          const [minutes, secs] = timeParts;
          seconds = minutes * 60 + secs;
        } else {
          const [firstPart] = timeParts;
          seconds = firstPart;
        }

        const displayTimestamp = formatTimeComponent(timeParts);

        const fullTimestamp = getFullTimestamp(timeParts);

        if (part.trim() === '' && parts[index + 1]?.trim() === '') {
          return [part];
        }

        return [
          part,
          <TimestampBox
            key={index}
            onClick={() => handleTimestampClick(seconds)}
            title={`Jump to ${fullTimestamp}`}
          >
            {displayTimestamp}
          </TimestampBox>,
        ];
      }
      return [part];
    });
  };

  const processMarkdownChildren = (children: React.ReactNode) => {
    if (Array.isArray(children)) {
      return children.map((child) => {
        if (typeof child === 'string') {
          const paragraphs = child.split(/\n\n+/);
          return paragraphs.map((paragraph, pIndex) => {
            const processedParagraph = convertTimestampsToLinks(paragraph);
            return (
              <Typography
                key={pIndex}
                component="p"
                variant="body1"
                sx={{
                  textAlign: 'left',
                  color: theme.palette.text.primary,
                  mb: pIndex < paragraphs.length - 1 ? 2 : 0,
                }}
              >
                {processedParagraph}
              </Typography>
            );
          });
        }
        return child;
      });
    }
    if (typeof children === 'string') {
      const paragraphs = children.split(/\n\n+/);
      return paragraphs.map((paragraph, pIndex) => {
        const processedParagraph = convertTimestampsToLinks(paragraph);
        return (
          <Typography
            key={pIndex}
            component="p"
            variant="body1"
            sx={{
              textAlign: 'left',
              color: theme.palette.text.primary,
              mb: pIndex < paragraphs.length - 1 ? 2 : 0,
            }}
          >
            {processedParagraph}
          </Typography>
        );
      });
    }
    return children;
  };

  const formatContent = (messageContent: string) => {
    const timestampRegex = /[[(]?\s*(?:(?:\d+:)?\d{2}|\d):\d{2}\s*[)\]]?/g;

    return messageContent
      .split('\n')
      .map((line, pIndex) => {
        const parts = line.split(timestampRegex);
        const timestamps = line.match(timestampRegex) || [];

        return (
          <Box key={pIndex} sx={{ mb: 1, '&:last-child': { mb: 0 } }}>
            {parts.map((part, idx) => {
              const timestamp = timestamps[idx - 1];
              let seconds = 0;

              if (timestamp) {
                const cleanMatch = timestamp.replace(/[[\]()\s]/g, '');
                const timeParts = cleanMatch.split(':').map(Number);
                if (timeParts.length === 3) {
                  const [hours, minutes, secs] = timeParts;
                  seconds = hours * 3600 + minutes * 60 + secs;
                } else {
                  const [minutes, secs] = timeParts.slice(-2);
                  seconds = minutes * 60 + secs;
                }
              }

              return (
                <React.Fragment key={idx}>
                  {part && (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ children }) => (
                          <Typography
                            variant="body1"
                            component="span"
                            sx={{
                              textAlign: 'left',
                              color: theme.palette.text.primary,
                              pl: 0,
                              m: 0,
                              display: 'inline',
                            }}
                          >
                            {children}
                          </Typography>
                        ),
                        code: ({ children }) => (
                          <code
                            style={{
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? theme.palette.background.paper
                                  : theme.palette.background.default,
                              padding: '2px 4px',
                              borderRadius: '4px',
                              fontFamily: 'monospace',
                              color: theme.palette.text.primary,
                            }}
                          >
                            {children}
                          </code>
                        ),
                      }}
                    >
                      {part.trim()}
                    </ReactMarkdown>
                  )}
                  {timestamp && (
                    <TimestampBox
                      onClick={() => handleTimestampClick(seconds)}
                      title={`Jump to ${seconds}s`}
                    >
                      {formatTimeComponent(
                        timestamp
                          .replace(/[[\]()\s]/g, '')
                          .split(':')
                          .map(Number)
                      )}
                    </TimestampBox>
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        );
      })
      .filter(Boolean);
  };

  const renderMessage = (
    { role, content }: { role: string; content: string },
    index: number
  ) => (
    <Box sx={{ marginBottom: '1rem' }}>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
        }}
        onMouseEnter={() => setHoveredIndex(index)}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        {getAvatar(role)}
        <Box
          sx={{
            backgroundColor: (() => {
              if (role !== 'user') return 'transparent';
              return theme.palette.mode === 'dark'
                ? alpha(theme.palette.secondary.light, 0.1)
                : theme.palette.background.default;
            })(),
            borderRadius: role === 'user' ? '0 10px 10px 10px' : '0px',
            padding: role === 'user' ? '1rem' : '0',
            position: 'relative',
            minWidth: '10rem',
            width: '100%',
          }}
        >
          {role === 'user' ? (
            <Typography
              variant="body1"
              sx={{ textAlign: 'left', color: theme.palette.text.primary }}
            >
              {convertTimestampsToLinks(content)}
            </Typography>
          ) : (
            <Box>{formatContent(content)}</Box>
          )}
          {hoveredIndex === index && role === 'assistant' && (
            <Box
              sx={{
                position: 'absolute',
                top: '5px',
                right: '5px',
                display: 'flex',
                gap: '0.5rem',
                backgroundColor: 'transparent',
                borderRadius: '4px',
              }}
            >
              <IconButton
                onClick={() => handleCopy(content, index)}
                title={copiedIndex === index ? 'Copied' : 'Copy'}
                sx={{
                  padding: '4px',
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                {copiedIndex === index ? (
                  <Check sx={{ fontSize: 20 }} />
                ) : (
                  <Copy sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

  const renderThinkingOrResponse = () => (
    <Box
      sx={{ marginBottom: '1rem', display: 'flex', alignItems: 'flex-start' }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <Collapse in={isThinking} timeout="auto" orientation="vertical">
          <Thinking />
        </Collapse>
        <Collapse
          in={!isThinking && expandingResponse !== null}
          timeout="auto"
          orientation="vertical"
          onExited={() => setShowFullResponse(true)}
        >
          {expandingResponse && (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children }) => (
                  <Typography
                    variant="body1"
                    sx={{
                      textAlign: 'left',
                      maxHeight: showFullResponse ? 'none' : '3rem',
                      overflow: 'hidden',
                      color: theme.palette.text.primary,
                    }}
                  >
                    {processMarkdownChildren(children)}
                  </Typography>
                ),
              }}
            >
              {expandingResponse}
            </ReactMarkdown>
          )}
        </Collapse>
        {!isThinking && expandingResponse && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              cursor: 'pointer',
              marginTop: '0.5rem',
            }}
            onClick={() => setShowFullResponse(!showFullResponse)}
          >
            <Typography variant="body2" color="primary">
              {showFullResponse ? 'Show Less' : 'Show More'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  if (conversation.length === 0) return <ChatHistoryEmpty />;

  return (
    <Box sx={{ mt: 2 }}>
      {conversation.map((message, index) => (
        <React.Fragment key={index}>
          {renderMessage(message, index)}
          {index === conversation.length - 1 &&
            message.role === 'user' &&
            renderThinkingOrResponse()}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default ChatHistory;
